import { Button, SearchBar, Select } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { packages, type PackageWithFeatureCodes } from "../../api/packages.service";
import type { ActiveFilter } from "../../components/DataFilters/DataFilters";
import { DataFilters } from "../../components/DataFilters/DataFilters";
import PackageGrid from "../../components/PackageGrid";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { useDeferredLoading } from "../../hooks/useDeferredLoading";
import styles from "./Packages.module.css";

const FILTER_CONFIGS = [
  {
    key: "status",
    label: "Status",
    options: [
      { value: "all", label: "All" },
      { value: "true", label: "Active" },
      { value: "false", label: "Inactive" },
    ],
  },
];

type PackageListContext = {
  refetchTrigger?: number;
  setListError?: (error: Error | null) => void;
};

function PackageList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<PackageListContext>();
  const [displayedPackages, setDisplayedPackages] = useState<PackageWithFeatureCodes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
  });
  const deferredLoading = useDeferredLoading(loading);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "" && value !== "all") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!hasSearched) return;
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
      updateURL({ search: searchInput, page: "1" });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, hasSearched]);

  function handleSearchChange(value: string) {
    setHasSearched(true);
    setSearchInput(value);
  }

  function handleFilterChange(key: string, value: string | number) {
    const stringValue = String(value);
    setFilters(prev => ({ ...prev, [key]: stringValue }));
    setCurrentPage(1);
    updateURL({ [key]: stringValue, page: "1" });
  }

  function handleClearFilter(key: string) {
    handleFilterChange(key, "all");
  }

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 10;
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
  }, [searchParams]);

  useEffect(() => {
    loadPackages();
  }, [currentPage, itemsPerPage, searchTerm, filters.status]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadPackages();
    }
  }, [context.refetchTrigger]);

  async function loadPackages() {
    try {
      setLoading(true);
      setError(null);
      if (context.setListError) {
        context.setListError(null);
      }
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await packages.getAll({
        productCode: searchTerm || undefined,
        isActive: filters.status === "all" ? undefined : filters.status === "true",
        limit: itemsPerPage,
        offset: offset,
      });
      setDisplayedPackages(response.packages);
      setTotal(response.pagination.total);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to load packages");
      setError(error);
      if (context.setListError) {
        context.setListError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startIndex = offset;
  const endIndex = offset + displayedPackages.length;

  const activeFilters: ActiveFilter[] = Object.entries(filters)
    .filter(([_, value]) => value !== "all")
    .map(([key, value]) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      value,
      displayValue: value === "true" ? "Active" : "Inactive",
    }));

  function handlePackageClick(pkg: PackageWithFeatureCodes) {
    navigate(`/fulfilment/packages/${pkg.id}`);
  }

  if (loading) {
    return <LoadingState message="Loading packages..." />;
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load packages"
        message={error.message}
        action={{
          label: "Retry",
          onClick: loadPackages,
        }}
      />
    );
  }

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search packages by code..."
          onClear={() => {
            setHasSearched(true);
            setSearchInput("");
          }}
        />
      </div>

      <div className={styles.controlsSection}>
        <div className="">
          <DataFilters
            filters={FILTER_CONFIGS}
            values={filters}
            onChange={handleFilterChange}
            activeFilters={activeFilters}
            onClearFilter={handleClearFilter}
          />
        </div>

        <div className={styles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        <PackageGrid
          packages={displayedPackages}
          onPackageClick={handlePackageClick}
          loading={deferredLoading}
        />

        {!deferredLoading && !error && displayedPackages.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="packages"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>
    </>
  );
}

export default PackageList;
